import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../../api/axios';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Rapprochement à 3 voies (TF-073) : confronte, par article, ce qui a été commandé / reçu /
 * facturé auprès d'un fournisseur, et met en évidence les écarts (facturé != commandé).
 * Auto-suffisant (types inline + appel API direct) pour rester additif.
 */
interface Ligne3Voies {
  articleId: number | null;
  designation: string;
  qteCommandee: number;
  montantCommande: number;
  qteRecue: number;
  qteFacturee: number;
  montantFacture: number;
  ecartQuantite: boolean;
  ecartMontant: boolean;
}

interface Rapprochement3Voies {
  fournisseurId: number;
  fournisseurNom: string;
  nbEcarts: number;
  lignes: Ligne3Voies[];
}

export function Rapprochement3VoiesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Rapprochement3Voies | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    api.get<Rapprochement3Voies>(`/achats/fournisseurs/${id}/rapprochement-3-voies`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (!data) return <div className="card">Rapprochement indisponible.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/fournisseurs/${id}`)} className="btn-outline p-2">
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
        <div>
          <h1 className="page-title">Rapprochement 3 voies — {data.fournisseurNom}</h1>
          <p className="page-subtitle">Commandé · reçu · facturé, par article</p>
        </div>
      </div>

      <div className={`card border-l-4 ${data.nbEcarts === 0 ? 'border-green-500' : 'border-red-500'}`}>
        <p className="font-semibold">
          {data.nbEcarts === 0
            ? '✓ Aucun écart : les factures concordent avec les commandes.'
            : `⚠ ${data.nbEcarts} article(s) en écart entre commande et facturation.`}
        </p>
      </div>

      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-medium text-gray-500 uppercase">
              <th className="px-3 py-2">Article</th>
              <th className="px-3 py-2 text-right">Qté commandée</th>
              <th className="px-3 py-2 text-right">Qté reçue</th>
              <th className="px-3 py-2 text-right">Qté facturée</th>
              <th className="px-3 py-2 text-right">Montant commandé</th>
              <th className="px-3 py-2 text-right">Montant facturé</th>
              <th className="px-3 py-2">État</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.lignes.map((l, i) => {
              const ecart = l.ecartQuantite || l.ecartMontant;
              return (
                <tr key={i} className={ecart ? 'bg-red-50' : 'hover:bg-gray-50'}>
                  <td className="px-3 py-2 font-medium text-gray-800">{l.designation}</td>
                  <td className="px-3 py-2 text-right">{l.qteCommandee}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{l.qteRecue}</td>
                  <td className={`px-3 py-2 text-right ${l.ecartQuantite ? 'text-red-600 font-semibold' : ''}`}>{l.qteFacturee}</td>
                  <td className="px-3 py-2 text-right"><MoneyDisplay amount={l.montantCommande} size="sm" /></td>
                  <td className={`px-3 py-2 text-right ${l.ecartMontant ? 'text-red-600 font-semibold' : ''}`}>
                    <MoneyDisplay amount={l.montantFacture} size="sm" />
                  </td>
                  <td className="px-3 py-2">
                    {ecart ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                        {l.ecartQuantite ? 'Qté' : ''}{l.ecartQuantite && l.ecartMontant ? ' + ' : ''}{l.ecartMontant ? 'Montant' : ''}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">OK</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Rapprochement3VoiesPage;
